<template>
  <div
    id="region-field"
    :class="label"
  >
    <div class="main-regions">
      <p
        class="main-region select-box click"
        v-for="(region, index) in Object.entries(mainRegions)"
        :key="index"
        :class="{ selected: selectedRegion?.value == region[0] }"
        @click="selectRegion({ value: region[0] })"
      >
        {{ mainRegions[region[0]] }}
      </p>
    </div>
    <v-select
      :class="label"
      :options="regions"
      :model-value="selectedRegion"
      :placeholder="placeholder"
      :clearable="false"
      @update:model-value="(option: Option) => selectRegion(option)"
    >
      <template #selected-option="option">
        <span class="truncate">{{ option.label }}</span>
      </template>
      <template #option="option">
        <span class="truncate"
          >{{ option.label }} {{ label == 'canton' ? '(' + option.value + ')' : '' }}</span
        >
      </template>
      <template #no-options>
        <span class="truncate">{{ $t('no_results') }}</span>
      </template>
    </v-select>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { useConfigStore, useRoadbookStore } from '@/store';
  import type { Option } from '@/models/interfaces';
  import toKey from '@/utils/toKey';
  import {
    CHE_OPTIONS,
    getSwitzerlandOSM,
  } from '@/services/administrativeDivisions/switzerland.service';
  import { ESP_OPTIONS, getSpannishOSM } from '@/services/administrativeDivisions/spain.service';
  import { BEL_OPTIONS, getBelgiumOSM } from '@/services/administrativeDivisions/belgium.service';
  import { CAN_OPTIONS, getCanadianOSM } from '@/services/administrativeDivisions/canada.service';

  const configStore = useConfigStore();
  const roadbookStore = useRoadbookStore();

  const { label = 'canton', placeholder = 'Your canton' } = defineProps<{
    label?: string;
    placeholder?: string;
  }>();

  const regions = computed<Array<Option>>(() => {
    if (label == 'canton') {
      return CHE_OPTIONS;
    } else if (label == 'provinceCan') {
      return CAN_OPTIONS;
    } else if (label == 'espRegions') {
      return ESP_OPTIONS;
    } else if (label == 'provinceBel') {
      return BEL_OPTIONS;
    } else {
      return [];
    }
  });

  const mainRegions = computed<{ GE?: string; VD?: string; VS?: string }>(() => {
    return configStore.mainCantons && label == 'canton' ? configStore.mainCantons : {};
  });

  const selectedRegion = ref<Option | null>();

  watch(
    [() => roadbookStore.department, () => roadbookStore.region],
    (payload) => {
      const aL6 = payload[0];
      const aL4 = payload[1];
      if (aL6) {
        selectedRegion.value = formatLabel(aL6);
      } else if (aL4) {
        selectedRegion.value = formatLabel(aL4);
      }
    },
    { immediate: true },
  );

  async function selectRegion({ value }: { value: string }): Promise<void> {
    roadbookStore.setRoadbookModifiedDate();
    let osm: { aL4: string; aL6: string } | null = null;
    switch (roadbookStore.country) {
      case 'ESP':
        osm = getSpannishOSM(value);
        break;
      case 'BEL':
        osm = getBelgiumOSM(value);
        break;
      case 'CHE':
        // value passed raw
        break;
      case 'CAN':
        // value passed raw
        break;
      default:
        break;
    }

    const aL4 = osm?.aL4 ?? '';
    const aL6 = osm?.aL6 ?? '';

    if (roadbookStore.country === 'CHE') {
      roadbookStore.updateMetadata({
        category: 'visitor',
        field: 'region',
        value: value,
      });
    } else if (roadbookStore.country === 'CAN') {
      roadbookStore.updateMetadata({
        category: 'visitor',
        field: 'region',
        value: value,
      });
    } else {
      roadbookStore.updateMetadata({
        category: 'visitor',
        field: 'department',
        value: toKey(aL6),
        skipFields: ['region'],
      });
      roadbookStore.updateMetadata({
        category: 'visitor',
        field: 'region',
        value: toKey(aL4),
        skipFields: ['department'],
      });
    }
  }

  function formatLabel(value: string): Option {
    let osm: { aL4: string; aL6: string } | null = null;
    switch (roadbookStore.country) {
      case 'ESP':
        osm = getSpannishOSM(value);
        break;
      case 'BEL':
        osm = getBelgiumOSM(value);
        break;
      case 'CHE':
        osm = getSwitzerlandOSM(value);
        break;
      case 'CAN':
        osm = getCanadianOSM(value);
        break;
      default:
        break;
    }
    const aL4 = osm?.aL4 ?? '';
    const aL6 = osm?.aL6 ?? '';
    return {
      value,
      label: aL6 ? `${aL4} (${aL6})` : aL4,
    };
  }
</script>

<style lang="postcss">
  #region-field {
    @apply flex-[0] flex flex-row flex-wrap;

    .main-regions {
      @apply flex flex-row mb-5;
    }

    .canton {
      @apply ml-5;
    }

    .remove-icon {
      height: 14px;
      path {
        stroke: none !important;
        fill: white;
      }
    }

    .vs__selected {
      @apply max-w-[calc(100%-35px)];
    }
  }
  .v-select {
    @apply v-select-layer;

    .vs__actions svg path {
      stroke: theme('colors.white') !important;
    }

    &.vs--open {
      .vs__selected {
        @apply top-[6px] z-10 flex absolute max-w-[calc(100%-35px)];
      }
    }
  }
  @media screen and (max-width: 890px) {
    #region-field {
      .canton {
        @apply ml-[8px];
      }
    }
  }
</style>
